import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  HeaderContainer,
  InputInfoPost,
  Basicnfo,
  InfoPost,
  UserAvatar,
  toolSelectStyles,
  subjectsSelectStyles,
  RightSideContent,
  LeftSideContent,
  ChallengeSubmit,
  SubmitHint,
} from './styles';
import {
  AddIcon,
  CardPlusIcon,
  ChallengeIcon,
  ChevronDownIcon,
  UploadIcon,
} from '../../../assets/svg/SVGComponents';
import CreatableSelect from '../../../components/Selects/CreatableSelect';
import { useAuth } from '../../../hooks/auth';
import Input from '../../../components/Input';
import Select from '../../../components/Selects/SimpleSelect';
import FileUploadInput from '../../../components/FileUploadInput';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import {
  Grades,
  gradeTexts,
  HardwareSlug,
  hardwareTexts,
} from '../../../utils/enums';
import api from '../../../services/api';
import IChallengeDataProps from '../../../dtos/IChallengeDataProps';
import ChallengeCardHorizontal from '../../../components/ChallengeCard/ChallengeCardHorizontal';
import { useImageModeration } from '../../../hooks/useImageModeration';

interface SubmitFormProject {
  grade: Grades;
  hardware: HardwareSlug;
  tags?: string[];
  title: string;
  description: string;
  attachments: File[];
}

const CreatePost: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const { challengeSlug } = useParams();
  const navigate = useNavigate();
  const [challengeSubmited, setChallengeSubmited] = useState(false);
  const [challengeData, setChallengeData] = useState<IChallengeDataProps>();
  const [challengesData, setChallengesData] = useState<IChallengeDataProps[]>();

  // ─── Image moderation (Phase 2) ──────────────────────────────────────
  const {
    entries: moderationEntries,
    analyzeFile,
    removeEntry: removeModerationEntry,
    hasPending,
    hasRejected,
    hasUnsupported,
    hasMediaAwaitingReview,
    approvedKeys,
  } = useImageModeration();

  const handleFileAdded = useCallback(
    (fileId: string, file: File) => {
      analyzeFile(fileId, file);
    },
    [analyzeFile],
  );

  const handleFileRemoved = useCallback(
    (fileId: string) => {
      removeModerationEntry(fileId);
    },
    [removeModerationEntry],
  );

  useEffect(() => {
    if (challengeSlug) {
      api.get(`/challenges/${challengeSlug}`).then(response => {
        setChallengeSubmited(true);
        setChallengeData(response.data);
      });
    }
    if (!challengeSlug) {
      api.get('/challenges/avaiable').then(response => {
        setChallengeSubmited(false);
        setChallengesData(response.data);
      });
    }
  }, [challengeSlug]);

  const GradeOptions = useMemo(() => {
    return Object.entries(gradeTexts).map(keyValue => ({
      label: keyValue[1],
      value: keyValue[0],
    }));
  }, []);

  const HardwareOptions = useMemo(() => {
    return Object.entries(hardwareTexts).map(keyValue => ({
      label: keyValue[1],
      value: keyValue[0],
    }));
  }, []);

  const projectSchema = useMemo(
    () =>
      Yup.object().shape({
        grade: Yup.string()
          .oneOf(Object.values(Grades), 'Selecione um ano válido.')
          .required('O ano é obrigatório.'),
        hardware: Yup.string()
          .oneOf(Object.values(HardwareSlug), 'Selecione um hardware válido.')
          .required('O kit usado é obrigatório.'),
        tags: Yup.array().of(Yup.string()), // tags é opcional
        title: Yup.string().required('O título é obrigatório.'),
        description: Yup.string().required('A descrição é obrigatória.'),
        attachments: Yup.array()
          .of(Yup.mixed())
          .min(1, 'Selecione pelo menos um arquivo.')
          .max(5, 'No máximo 5 arquivos permitidos.')
          .required('É necessário pelo menos uma foto ou vídeo no seu post.'),
      }),
    [],
  );

  const handleSubmit = useCallback(
    async (data: SubmitFormProject) => {
      formRef.current?.setErrors({});
      setLoading(true);

      try {
        const validatePost = await projectSchema.validate(data, {
          abortEarly: false,
          stripUnknown: true,
        });

        const formData = new FormData();

        if (validatePost) {
          formData.append('grade', validatePost.grade);
          formData.append('hardware', validatePost.hardware);
          formData.append('title', validatePost.title);
          formData.append('description', validatePost.description);

          validatePost.tags?.forEach(tagValidate => {
            if (tagValidate !== undefined) {
              formData.append('tags[]', tagValidate);
            }
          });

          // Só publicamos o que passou pela moderação. O envio multipart cru
          // continua existindo no backend por compatibilidade, mas usá-lo aqui
          // publicaria arquivos que o Rekognition nunca chegou a analisar —
          // exatamente o que acontecia quando nenhuma imagem era aprovada.
          approvedKeys.forEach(key => {
            formData.append('upload_keys[]', key);
          });
        }

        if (challengeData) {
          formData.append('challenge_slug', challengeData.slug);
        }

        const response = await api.post(`/posts/create`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.status === 200) {
          // Navegar para a página da comunidade quando a requisição for bem sucedida
          navigate('/community');
        }
        // Prosseguir com a submissão os dados
      } catch (err) {
        // Tratar erros de validação
        if (err instanceof Yup.ValidationError) {
          const errors = err.inner.reduce((acc: any, error) => {
            acc[error.path] = error.message;
            return acc;
          }, {});
          formRef.current?.setErrors(errors);
        }
      } finally {
        setLoading(false);
      }
    },
    [projectSchema, approvedKeys, challengeData, navigate],
  );

  const formatCreateLabel = useCallback((inputValue: string) => {
    return (
      <p>
        <b>Criar nova:</b> {inputValue}
      </p>
    );
  }, []);

  // Arquivo de formato ilegível não bloqueia o post — ele só não é enviado.
  // O que bloqueia é não sobrar nenhuma mídia aprovada para publicar.
  const noApprovedFiles = approvedKeys.length === 0;
  const submitBlocked = hasPending || hasRejected || noApprovedFiles;

  const submitLabel = useMemo(() => {
    if (hasPending) return 'Verificando arquivos…';
    if (hasRejected) return 'Remova os arquivos rejeitados';
    if (noApprovedFiles) return 'Adicione ao menos uma foto ou vídeo';
    return 'Publicar projeto';
  }, [hasPending, hasRejected, noApprovedFiles]);

  return (
    <Container>
      <LeftSideContent>
        <HeaderContainer>
          <CardPlusIcon
            width="2rem"
            stroke="#FF9800"
            strokeWidth={3}
            fill="none"
          />
          <h1>Publique seu projeto</h1>
        </HeaderContainer>

        <InputInfoPost>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Basicnfo>
              <header>
                <UserAvatar
                  avatarSrc={
                    user.avatar_url ||
                    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                  }
                />
                <div>
                  <h2>{user.name}</h2>
                  <span>{user.school?.name}</span>
                </div>
              </header>
              <section>
                <div>
                  <h4>Nível de escolaridade*</h4>
                  <Select
                    name="grade"
                    placeholder="Escolaridade do projeto"
                    dropdownIcon={ChevronDownIcon}
                    options={GradeOptions}
                    styles={toolSelectStyles}
                  />
                </div>
                <div>
                  <h4>Ferramenta utilizada*</h4>
                  <Select
                    name="hardware"
                    placeholder="Kit utilizado"
                    dropdownIcon={ChevronDownIcon}
                    options={HardwareOptions}
                    styles={toolSelectStyles}
                  />
                </div>
              </section>

              <div>
                <h4>Palavras chaves </h4>
                <CreatableSelect
                  name="tags"
                  isMulti
                  placeholder="Palavras chaves"
                  dropdownIcon={AddIcon}
                  styles={subjectsSelectStyles}
                  isClearable
                  formatCreateLabel={formatCreateLabel}
                />
              </div>
            </Basicnfo>
            <InfoPost>
              <div>
                <h4>Título*</h4>
                <Input name="title" placeholder="Título do projeto" />
              </div>
              <div>
                <h4>Descrição*</h4>
                <Input
                  name="description"
                  placeholder="Resumo breve do projeto"
                  multiline
                />
              </div>

              <div>
                <h4>Arquivos*</h4>
                <FileUploadInput
                  name="attachments"
                  onFileAdded={handleFileAdded}
                  onFileRemoved={handleFileRemoved}
                  moderationEntries={moderationEntries}
                />
              </div>
              {/* upload fotos */}
            </InfoPost>

            {hasUnsupported && (
              <SubmitHint>
                Os arquivos marcados como “Não enviado” ficam de fora da
                publicação. O resto do projeto pode ser publicado normalmente.
              </SubmitHint>
            )}

            {hasMediaAwaitingReview && (
              <SubmitHint>
                Seu projeto entra no ar assim que você publicar. Um dos arquivos
                passa por avaliação da nossa equipe antes de aparecer para todo
                mundo — o resto do post não espera por isso.
              </SubmitHint>
            )}

            <PrimaryButton
              type="submit"
              loading={loading}
              disabled={submitBlocked}
            >
              <UploadIcon width="1.4rem" stroke="#fff" strokeWidth={3} />
              {submitLabel}
            </PrimaryButton>
          </Form>
        </InputInfoPost>
      </LeftSideContent>
      <RightSideContent>
        {!!challengeSubmited && challengeData && (
          <ChallengeSubmit>
            <h1>
              <ChallengeIcon
                width="2.5rem"
                fillLeafColor="#ff9800"
                strokeStarColor="#ff9800"
              />
              Desafio
            </h1>
            <ChallengeCardHorizontal
              id={challengeData.id}
              illustration_url={challengeData.illustration_url}
              slug={challengeData.slug}
              title={challengeData.title}
              description={challengeData.description}
              start_submission={challengeData.start_submission}
              end_submission={challengeData.end_submission}
              count_submissions={challengeData.count_submissions}
              status={challengeData.status}
              type="to-submit"
            />
          </ChallengeSubmit>
        )}
        {!challengeSubmited && challengesData && (
          <ChallengeSubmit>
            <h1>
              <ChallengeIcon
                width="2.5rem"
                fillLeafColor="#ff9800"
                strokeStarColor="#ff9800"
              />
              Desafios que você pode participar
            </h1>
            {challengesData.slice(0, 3).map(challenge => (
              <ChallengeCardHorizontal
                key={challenge.id}
                {...challenge}
                type="submit"
              />
            ))}
          </ChallengeSubmit>
        )}
      </RightSideContent>
    </Container>
  );
};

export default CreatePost;
